// apps/web/src/components/painel/home/home-perfil.tsx — Fase 17.8, Plano 04 (D-01, D-13; UI-SPEC U-01, U-04, U-26).
// Composição das regiões de uma aba migrada: o bloco A do hero (a métrica herói) e, no Plano 06, o bloco B (até 3 números
// de apoio, `painel-apoio`) e, no Plano 07, a linha de painéis com "Precisa de você"; no Plano 08, o segundo painel da linha é a
// esteira "Fluxo agora" com a assinatura do perfil (Cabines) dentro. Trata o resultado da carga da home: carregando (esqueleto), erro (região com nova tentativa,
// mantendo os dados anteriores quando houver), 403 (sem acesso) e sucesso. Ao vivo (Plano 12): `origem` decide o rolo dos números
// ('ao-vivo') e `brilhos` marca os cards que mudaram (`data-brilho` por ~1 s; com movimento reduzido, `data-brilho-estatico`).
import type { ReactElement } from 'react';
import { PackageCheck, ShieldCheck, ShoppingCart, type LucideIcon } from 'lucide-react';
import type { Assinatura, PainelHomeResponsePorPerfil, PerfilHome, PerfilHomeComResposta, Regiao } from '@pieltec/contracts';
import { usePrefersReducedMotion } from '../../../hooks/use-prefers-reduced-motion';
import { atributosDoBrilho } from '../../../lib/painel/home/brilho';
import type { ResultadoHome } from '../../../lib/painel/home/carga-home';
import { CATALOGO_METRICAS, rotuloDoHeroi } from '../../../lib/painel/home/catalogo-metricas';
import { ApoioKv } from './apoio-kv';
import { PainelAreas, type PainelAreasProps } from './assinatura-areas';
import { AssinaturaCabines } from './assinatura-cabines';
import { AssinaturaCoberturaPo } from './assinatura-cobertura-po';
import { AssinaturaEmPoder } from './assinatura-em-poder';
import { AssinaturaNcsPorEtapa } from './assinatura-ncs-por-etapa';
import { AssinaturaPoEstoque } from './assinatura-po-estoque';
import { Esteira } from './esteira';
import { HeroiMetrica } from './heroi-metrica';
import type { OrigemNumero } from './numero-ao-vivo';
import { PrecisaDeVoce } from './precisa-de-voce';
import { RegiaoCarregando, RegiaoErro, RegiaoSemAcesso } from './regiao-estado';
import { SaudeSistema } from './saude-sistema';
import '../../../styles/painel-home.css';

/** Resposta de uma home já migrada (cresce com `PainelHomeResponsePorPerfil`). */
export type RespostaHome = PainelHomeResponsePorPerfil[PerfilHomeComResposta];

/** Altura final do hero (U-26), para o esqueleto não deslocar o layout. */
const ALTURA_HEROI = 320;

/** Perfis cuja home já tem o bloco B de apoio: o esqueleto reserva o lugar para o hero não mudar de colunas ao carregar. */
const PERFIS_COM_APOIO: ReadonlySet<PerfilHome> = new Set<PerfilHome>(['pcp', 'qualidade', 'almoxarifado', 'compras', 'faturamento', 'diretoria']);

/** Perfis cuja home já traz a lista "Precisa de você" (Plano 07): o esqueleto reserva a linha de painéis. */
const PERFIS_COM_LISTA: ReadonlySet<PerfilHome> = new Set<PerfilHome>(['pcp', 'qualidade', 'almoxarifado', 'compras', 'faturamento', 'diretoria']);

/** Esteira de cada perfil: título, link do cabeçalho, linha amigável de esteira zerada e estilo da nota do gargalo (o PCP é o padrão). */
interface ConfiguracaoDaEsteira {
  readonly titulo: string;
  readonly link: { readonly rotulo: string; readonly href: string };
  readonly textoVazio?: string;
  readonly estiloNota?: 'concentra' | 'gargalo';
  /** Ícone da linha amigável de esteira zerada. */
  readonly iconeVazio?: LucideIcon;
  /** Limite de "parado" citado na nota do gargalo (padrão '24 h'). */
  readonly limiteParado?: string;
}

const ESTEIRA_PADRAO: ConfiguracaoDaEsteira = { titulo: 'Fluxo agora', link: { rotulo: 'Abrir Kanban', href: '/planejamento/kanban' } };

const ESTEIRA_DO_PERFIL: Readonly<Partial<Record<PerfilHome, ConfiguracaoDaEsteira>>> = {
  almoxarifado: {
    titulo: 'Abastecimento das cabines',
    link: { rotulo: 'Abrir fila', href: '/estoque/abastecimento' },
    textoVazio: 'A fila está vazia. Tudo entregue.',
    iconeVazio: PackageCheck,
    estiloNota: 'gargalo',
    limiteParado: '60 min',
  },
  // O limite de "parado" (7 dias) vem da resposta (`fatos.limiteDias`) e substitui o texto padrão abaixo na hora de montar a nota.
  qualidade: {
    titulo: 'Esteira da qualidade',
    link: { rotulo: 'Abrir não conformidades', href: '/qualidade/nao-conformidades' },
    textoVazio: 'Nenhuma não conformidade na esteira da qualidade agora.',
    iconeVazio: ShieldCheck,
    estiloNota: 'gargalo',
  },
  // O limite de "parado" (7 dias) vem da resposta (`fatos.limiteDiasParado`) e substitui o texto padrão na hora de montar a nota.
  compras: {
    titulo: 'Esteira de compras',
    link: { rotulo: 'Abrir pedidos de compra', href: '/compras/pedidos' },
    textoVazio: 'Nenhum pedido de compra em aberto.',
    iconeVazio: ShoppingCart,
    estiloNota: 'gargalo',
  },
  faturamento: {
    titulo: 'Esteira do faturamento',
    link: { rotulo: 'Abrir pedidos', href: '/vendas/pedidos' },
    textoVazio: 'Nenhum pedido na esteira do faturamento agora.',
    estiloNota: 'gargalo',
  },
};

/** Nome da assinatura de cada perfil na mensagem de erro da região ("Não foi possível carregar ..."). */
const NOME_DA_ASSINATURA: Readonly<Partial<Record<PerfilHome, string>>> = {
  almoxarifado: 'o pó em estoque',
  qualidade: 'as NCs por etapa',
  compras: 'a cobertura do pó',
  faturamento: 'as peças em poder da Pieltec',
  diretoria: 'a empresa hoje',
};

/**
 * Limite de "parado" citado na nota do gargalo quando ele vem da resposta (Qualidade: a NC aberta há mais de `limiteDias` dias; Compras:
 * o pedido ou a nota parados há mais de `limiteDiasParado` dias).
 */
function limiteParadoDe(dados: RespostaHome): string | undefined {
  const dias = dados.fatos.perfil === 'qualidade' ? dados.fatos.limiteDias : dados.fatos.perfil === 'compras' ? dados.fatos.limiteDiasParado : undefined;
  return dias === undefined ? undefined : `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
}

/** A assinatura da Diretoria (as áreas e o ranking); qualquer outra variante não é dela e vira "sem medição". */
function regiaoDeAreas(regiao: Regiao<Assinatura>): PainelAreasProps['regiao'] {
  if (regiao.estado !== 'MEDIDO') return regiao;
  return regiao.tipo === 'areas' ? regiao : { estado: 'SEM_MEDICAO' };
}

export interface DadosAnteriores {
  readonly dados: RespostaHome;
  readonly em: Date;
}

export interface HomePerfilProps {
  readonly perfil: PerfilHome;
  /** `null` enquanto a carga está em andamento. */
  readonly resultado: ResultadoHome<RespostaHome> | null;
  /** Últimos dados carregados com sucesso: continuam na tela durante a nova tentativa e quando ela falha. */
  readonly anterior?: DadosAnteriores;
  readonly permissoes: readonly string[];
  /** Verdadeiro na primeira carga: contagem do numeral, desenho da sparkline e entrada da faixa. */
  readonly primeiraCarga?: boolean;
  /** Origem dos dados atuais: 'ao-vivo' rola os dígitos; sem ela vale `primeiraCarga` (contagem) ou a troca silenciosa. */
  readonly origem?: OrigemNumero;
  /** Ids dos cards que brilham agora ('heroi', 'apoio-N', 'etapa-{id}', 'item-{id}'): só chegam em mudança vinda do stream. */
  readonly brilhos?: readonly string[];
  /** Recarrega só esta região. */
  readonly onTentarNovamente: () => void;
}

export function HomePerfil({ perfil, resultado, anterior, permissoes, primeiraCarga = true, origem, brilhos, onTentarNovamente }: HomePerfilProps): ReactElement {
  const reduzido = usePrefersReducedMotion();
  const origemAtual: OrigemNumero = origem ?? (primeiraCarga ? 'primeira-carga' : 'silenciosa');
  const esteiraDoPerfil = ESTEIRA_DO_PERFIL[perfil] ?? ESTEIRA_PADRAO;

  const heroi = (dados: RespostaHome, primeira: boolean, origemDosDados: OrigemNumero): ReactElement => (
    <HeroiMetrica
      rotulo={rotuloDoHeroi(perfil, dados.heroi, permissoes)}
      metrica={dados.heroi}
      carregando={false}
      permissoes={permissoes}
      primeiraCarga={primeira}
      origem={origemDosDados}
      brilho={atributosDoBrilho('heroi', brilhos, reduzido)}
      onTentarNovamente={onTentarNovamente}
    />
  );

  // Bloco B (Plano 06): os kv que a API devolveu, na ordem do perfil; kv omitido pela API (sem permissão) simplesmente não existe.
  const apoio = (dados: RespostaHome, origemDosDados: OrigemNumero): ReactElement | null =>
    dados.apoio.length === 0 ? null : (
      <div className="painel-apoio" data-testid="painel-apoio">
        {dados.apoio.map((item, posicao) => {
          const indice = CATALOGO_METRICAS[item.id]?.chip ?? posicao + 1;
          return (
            <ApoioKv
              key={item.id}
              indice={indice}
              item={item}
              permissoes={permissoes}
              origem={origemDosDados}
              brilho={atributosDoBrilho(`apoio-${indice}`, brilhos, reduzido)}
              onTentarNovamente={onTentarNovamente}
            />
          );
        })}
      </div>
    );

  // Assinatura do perfil (Plano 08), por `tipo`: hoje só as cabines do PCP; os outros perfis acrescentam as variantes.
  const assinatura = (regiao: Regiao<Assinatura>): ReactElement | null => {
    if (regiao.estado === 'INDISPONIVEL') {
      return <RegiaoErro id="assinatura" nome={NOME_DA_ASSINATURA[perfil] ?? 'as cabines'} onTentarNovamente={onTentarNovamente} />;
    }
    if (regiao.estado === 'SEM_MEDICAO') return null;
    if (regiao.tipo === 'em-poder') return <AssinaturaEmPoder clientes={regiao.clientes} totalClientes={regiao.totalClientes} cobertura={regiao.cobertura} />;
    if (regiao.tipo === 'po-estoque') return <AssinaturaPoEstoque cores={regiao.cores} totalCores={regiao.totalCores} />;
    if (regiao.tipo === 'cobertura-po') return <AssinaturaCoberturaPo cores={regiao.cores} totalCores={regiao.totalCores} limiteDias={regiao.limiteDias} />;
    if (regiao.tipo === 'ncs-por-etapa') return <AssinaturaNcsPorEtapa origens={regiao.origens} limiteDias={regiao.limiteDias} permissoes={permissoes} />;
    if (regiao.tipo === 'areas') return null; // as áreas da Diretoria são o painel inteiro (PainelAreas), nunca a assinatura de uma esteira
    return <AssinaturaCabines cabines={regiao.cabines} permissoes={permissoes} />;
  };

  // Linha de painéis (Planos 07 e 08): "Precisa de você" com o instante dos próprios dados como referência do "quando" e, ao lado,
  // a esteira "Fluxo agora" com a assinatura do perfil dentro.
  const painelLista = (dados: RespostaHome, origemDosDados: OrigemNumero): ReactElement | null => {
    const aoVivo = origemDosDados === 'ao-vivo';
    const lista = dados.precisaDeVoce.estado === 'SEM_MEDICAO' ? null : (
      <PrecisaDeVoce perfil={perfil} regiao={dados.precisaDeVoce} permissoes={permissoes} agora={new Date(dados.geradoEm)} onTentarNovamente={onTentarNovamente} brilhos={brilhos} aoVivo={aoVivo} reduzido={reduzido} />
    );
    // Diretoria: o segundo painel é "A empresa hoje" (as áreas e o ranking de clientes), no lugar da esteira.
    if (dados.perfil === 'diretoria') {
      const areas = regiaoDeAreas(dados.assinatura);
      return (
        <>
          <div className="painel-paineis" data-testid="painel-paineis">
            {lista}
            <PainelAreas regiao={areas} permissoes={permissoes} onTentarNovamente={onTentarNovamente} />
          </div>
          {/* Plano 21 (D-03): "Saúde do sistema" só existe quando a API a devolveu (users:manage); sem ela nada é renderizado. */}
          <SaudeSistema saude={dados.saude} permissoes={permissoes} agora={new Date(dados.geradoEm)} />
        </>
      );
    }
    return (
      <div className="painel-paineis" data-testid="painel-paineis">
        {lista}
        <Esteira
          titulo={esteiraDoPerfil.titulo}
          linkCabecalho={esteiraDoPerfil.link}
          regiao={dados.esteira}
          permissoes={permissoes}
          onTentarNovamente={onTentarNovamente}
          origem={aoVivo ? 'ao-vivo' : 'silenciosa'}
          brilhos={brilhos}
          reduzido={reduzido}
          textoVazio={esteiraDoPerfil.textoVazio}
          estiloNota={esteiraDoPerfil.estiloNota}
          iconeVazio={esteiraDoPerfil.iconeVazio}
          limiteParado={limiteParadoDe(dados) ?? esteiraDoPerfil.limiteParado}
        >
          {assinatura(dados.assinatura)}
        </Esteira>
      </div>
    );
  };

  let conteudo: ReactElement;
  let paineis: ReactElement | null = null;
  if (resultado === null) {
    conteudo =
      anterior !== undefined ? (
        <>
          {heroi(anterior.dados, false, 'silenciosa')}
          {apoio(anterior.dados, 'silenciosa')}
        </>
      ) : (
        <>
          <RegiaoCarregando id="heroi" altura={ALTURA_HEROI} />
          {PERFIS_COM_APOIO.has(perfil) && <RegiaoCarregando id="apoio" altura={ALTURA_HEROI} />}
        </>
      );
    if (anterior !== undefined) paineis = painelLista(anterior.dados, 'silenciosa');
    else if (PERFIS_COM_LISTA.has(perfil)) {
      paineis = (
        <>
          <div className="painel-paineis" data-testid="painel-paineis">
            <PrecisaDeVoce perfil={perfil} regiao={null} permissoes={permissoes} agora={new Date()} onTentarNovamente={onTentarNovamente} />
            {perfil === 'diretoria' ? <PainelAreas regiao={null} permissoes={permissoes} /> : <Esteira titulo={esteiraDoPerfil.titulo} regiao={null} permissoes={permissoes} />}
          </div>
          {/* O esqueleto da saúde só para quem tem users:manage (quem não tem nunca recebe a faixa): reserva o lugar sem deslocar o layout. */}
          {perfil === 'diretoria' && permissoes.includes('users:manage') && <SaudeSistema carregando permissoes={permissoes} agora={new Date()} />}
        </>
      );
    }
  } else if (resultado.tipo === 'sem-acesso') {
    conteudo = <RegiaoSemAcesso />;
  } else if (resultado.tipo === 'sucesso') {
    conteudo = (
      <>
        {heroi(resultado.dados, primeiraCarga, origemAtual)}
        {apoio(resultado.dados, origemAtual)}
      </>
    );
    paineis = painelLista(resultado.dados, origemAtual);
  } else {
    // erro e sessão expirada (401): a região oferece nova tentativa; com dados anteriores, mantém os números.
    conteudo = (
      <>
        <div className="flex min-w-0 flex-col gap-4">
          <RegiaoErro id="heroi" nome="o indicador principal" onTentarNovamente={onTentarNovamente} dadosDe={anterior?.em} />
          {anterior !== undefined && heroi(anterior.dados, false, 'silenciosa')}
        </div>
        {anterior !== undefined && apoio(anterior.dados, 'silenciosa')}
      </>
    );
    if (anterior !== undefined) paineis = painelLista(anterior.dados, 'silenciosa');
  }

  return (
    <>
      <div className="painel-heroi-grade" data-testid={`painel-home-${perfil}`}>
        {conteudo}
      </div>
      {paineis}
    </>
  );
}
